import type { AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client.js";
import type { FeedbackFilters, FeedbackItem } from "../api/feedback.js";
import { can } from "../capabilities.js";
import { asMoment } from "../format.js";
import { useLanguage } from "../i18n.js";
import "./feedback.css";

const CHANGED = "oasis-feedback-reviewed";
export function FeedbackBadge() {
  const french = useLanguage() === "fr";
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let controller: AbortController | undefined;
    const load = () => {
      if (document.visibilityState === "hidden" || controller) return;
      const active = new AbortController();
      controller = active;
      api
        .feedbackSummary(active.signal)
        .then((result) => {
          if (!active.signal.aborted) setCount(result.newCount);
        })
        .catch(() => {
          if (!active.signal.aborted) setCount(null);
        })
        .finally(() => {
          if (controller === active) controller = undefined;
        });
    };
    load();
    const timer = window.setInterval(load, 60000);
    document.addEventListener("visibilitychange", load);
    window.addEventListener(CHANGED, load);
    return () => {
      controller?.abort();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", load);
      window.removeEventListener(CHANGED, load);
    };
  }, []);
  return count === null ? (
    <span
      className="feedback-badge"
      title={french ? "Nombre de nouveaux messages indisponible" : "New-message count unavailable"}
    >
      —
    </span>
  ) : count > 0 ? (
    <span
      className="feedback-badge"
      aria-label={`${count} ${french ? "nouveaux messages" : "new submissions"}`}
      role="status"
    >
      {count > 999 ? "999+" : count}
    </span>
  ) : null;
}

export function Feedback({ admin }: { admin: AuthenticatedAdmin }) {
  const french = useLanguage() === "fr";
  const words = french
    ? {
        status: "Boîte de réception",
        category: "Catégorie",
        new: "À traiter",
        reviewed: "Examinés",
        reviewedUnreplied: "Examinés sans réponse",
        repliedInbox: "Réponses envoyées",
        all: "Tous",
        suggestion: "Suggestion",
        bug: "Problème technique",
        complaint: "Réclamation",
        refresh: "Actualiser",
        search: "Rechercher",
        searchPlaceholder: "Joueur, message ou référence",
        searchAction: "Rechercher",
        clearSearch: "Effacer la recherche",
        more: "Afficher la suite",
        loading: "Chargement…",
        empty: "Aucun message dans cette vue.",
        failure: "Impossible de charger les messages. Réessayez.",
        saveFailure: "Modification non enregistrée. Réessayez.",
        mark: "Marquer comme examiné",
        reopen: "Remettre dans À traiter",
        details: "Détails techniques et référence",
        note: "Une réponse est envoyée au joueur dans Oasis. Ne saisissez aucune note interne dans la réponse.",
        author: "Compte",
        reference: "Référence",
        reviewer: "Examiné par",
        saved: "État enregistré.",
        reply: "Répondre au joueur",
        replyLabel: "Votre réponse",
        replyHint: "Visible par le joueur dans Oasis. 2 à 2 000 caractères.",
        sendReply: "Envoyer la réponse",
        cancel: "Annuler",
        replied: "Réponse envoyée",
        awaitingReply: "En attente de réponse",
        reviewedNoReply: "Examiné sans réponse",
        replyMoved: "Réponse envoyée. Ce message se trouve maintenant dans Réponses envoyées.",
        viewReviewed: "Voir les réponses envoyées",
      }
    : {
        status: "Inbox",
        category: "Category",
        new: "Needs attention",
        reviewed: "Reviewed",
        reviewedUnreplied: "Reviewed without reply",
        repliedInbox: "Replies sent",
        all: "All",
        suggestion: "Suggestion",
        bug: "Technical problem",
        complaint: "Complaint",
        refresh: "Refresh",
        search: "Search feedback",
        searchPlaceholder: "Player, message, or reference",
        searchAction: "Search",
        clearSearch: "Clear search",
        more: "Load older submissions",
        loading: "Loading…",
        empty: "No submissions in this view.",
        failure: "Could not load feedback. Please retry.",
        saveFailure: "Change was not saved. Please retry.",
        mark: "Mark reviewed",
        reopen: "Move to Needs attention",
        details: "Technical details and reference",
        note: "Replies are delivered to the player in Oasis. Do not put internal notes in a reply.",
        author: "Account",
        reference: "Reference",
        reviewer: "Reviewed by",
        saved: "Status saved.",
        reply: "Reply to player",
        replyLabel: "Your reply",
        replyHint: "Visible to the player in Oasis. 2–2,000 characters.",
        sendReply: "Send reply",
        cancel: "Cancel",
        replied: "Reply sent",
        awaitingReply: "Awaiting reply",
        reviewedNoReply: "Reviewed without reply",
        replyMoved: "Reply sent. This message is now in Replies sent.",
        viewReviewed: "View sent replies",
      };
  const [filters, setFilters] = useState<FeedbackFilters>({ status: "new" });
  const [searchDraft, setSearchDraft] = useState("");
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [error, setError] = useState<"load" | "save" | null>(null);
  const [saved, setSaved] = useState(false);
  const [justRepliedId, setJustRepliedId] = useState<string | null>(null);
  const active = useRef<AbortController | null>(null);
  const saveLock = useRef(false);
  const mounted = useRef(true);
  const load = useCallback(async (query: FeedbackFilters, append = false) => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setPending(true);
    setError(null);
    if (!append) {
      setItems([]);
      setCursor(null);
      setJustRepliedId(null);
    }
    try {
      const page = await api.feedback(query, controller.signal);
      if (controller.signal.aborted) return;
      setItems((current) =>
        append
          ? [
              ...current,
              ...page.items.filter((item) => !current.some((existing) => existing.id === item.id)),
            ]
          : page.items,
      );
      setCursor(page.nextCursor);
    } catch {
      if (!controller.signal.aborted) setError("load");
    } finally {
      if (!controller.signal.aborted) setPending(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      active.current?.abort();
    };
  }, []);
  useEffect(() => {
    setSaved(false);
    void load(filters);
    return () => active.current?.abort();
  }, [filters, load]);
  async function review(item: FeedbackItem) {
    if (saveLock.current) return;
    saveLock.current = true;
    setSaving(item.id);
    setError(null);
    setSaved(false);
    try {
      const result = await api.reviewFeedback(item.id, !item.reviewedAt);
      window.dispatchEvent(new Event(CHANGED));
      if (!mounted.current) return;
      setItems((current) =>
        filters.status === "all"
          ? current.map((row) => (row.id === item.id ? { ...row, ...result } : row))
          : current.filter((row) => row.id !== item.id),
      );
      setSaved(true);
    } catch {
      if (mounted.current) setError("save");
    } finally {
      saveLock.current = false;
      if (mounted.current) setSaving(null);
    }
  }
  async function sendReply(item: FeedbackItem) {
    if (saveLock.current || replyDraft.trim().length < 2 || replyDraft.trim().length > 2000) return;
    saveLock.current = true;
    setSaving(item.id);
    setError(null);
    setSaved(false);
    try {
      const result = await api.replyToFeedback(item.id, replyDraft.trim());
      window.dispatchEvent(new Event(CHANGED));
      if (!mounted.current) return;
      setItems((current) =>
        current.map((row) => (row.id === item.id ? { ...row, ...result } : row)),
      );
      setJustRepliedId(item.id);
      setReplyingId(null);
      setReplyDraft("");
    } catch {
      if (mounted.current) setError("save");
    } finally {
      saveLock.current = false;
      if (mounted.current) setSaving(null);
    }
  }
  return (
    <section className="body feedback-inbox">
      <div className="card feedback-filters">
        <label className="field">
          {words.status}
          <select
            className="input"
            disabled={Boolean(saving)}
            value={filters.status}
            onChange={(event) =>
              setFilters({ ...filters, status: event.target.value as FeedbackFilters["status"] })
            }
          >
            <option value="new">{words.new}</option>
            <option value="reviewed_unreplied">{words.reviewedUnreplied}</option>
            <option value="replied">{words.repliedInbox}</option>
            <option value="all">{words.all}</option>
          </select>
        </label>
        <label className="field">
          {words.category}
          <select
            className="input"
            disabled={Boolean(saving)}
            value={filters.category ?? ""}
            onChange={(event) =>
              setFilters({
                status: filters.status,
                ...(filters.search ? { search: filters.search } : {}),
                ...(event.target.value
                  ? { category: event.target.value as FeedbackItem["category"] }
                  : {}),
              })
            }
          >
            <option value="">{words.all}</option>
            {(["suggestion", "bug", "complaint"] as const).map((category) => (
              <option key={category} value={category}>
                {words[category]}
              </option>
            ))}
          </select>
        </label>
        <form
          className="feedback-search"
          onSubmit={(event) => {
            event.preventDefault();
            const search = searchDraft.trim();
            if (search.length === 1) return;
            const next = { ...filters };
            delete next.search;
            if (search) next.search = search;
            setFilters(next);
          }}
        >
          <label className="field" htmlFor="feedback-search-input">
            {words.search}
            <input
              id="feedback-search-input"
              className="input"
              type="search"
              value={searchDraft}
              maxLength={100}
              placeholder={words.searchPlaceholder}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </label>
          <button
            type="submit"
            className="btn btn-quiet"
            disabled={searchDraft.trim().length === 1}
          >
            {words.searchAction}
          </button>
          {filters.search ? (
            <button
              type="button"
              className="btn btn-quiet"
              onClick={() => {
                setSearchDraft("");
                const next = { ...filters };
                delete next.search;
                setFilters(next);
              }}
            >
              {words.clearSearch}
            </button>
          ) : null}
        </form>
        <button
          type="button"
          className="btn btn-quiet"
          disabled={pending || Boolean(saving)}
          onClick={() => void load(filters)}
        >
          {words.refresh}
        </button>
      </div>
      <p className="dim">{words.note}</p>
      {error ? (
        <p className="banner" role="alert">
          {error === "load" ? words.failure : words.saveFailure}
        </p>
      ) : null}
      {saved ? <p role="status">{words.saved}</p> : null}
      {items.map((item) => (
        <article className="card feedback-entry" key={item.id}>
          <header>
            <span className="chip">{words[item.category]}</span>
            <strong>{item.nickname || item.userId}</strong>
            <time dateTime={item.createdAt}>{asMoment(item.createdAt)}</time>
            <span
              className={`chip feedback-entry-state ${item.reply ? "is-replied" : item.reviewedAt ? "is-reviewed" : "is-new"}`}
            >
              {item.reply
                ? words.replied
                : item.reviewedAt
                  ? words.reviewedNoReply
                  : words.awaitingReply}
            </span>
          </header>
          <p className="feedback-message">{item.message}</p>
          {justRepliedId === item.id && filters.status === "new" ? (
            <div className="feedback-reply-confirmation" role="status">
              <span>{words.replyMoved}</span>
              <button
                type="button"
                className="btn btn-quiet"
                onClick={() => setFilters({ ...filters, status: "replied" })}
              >
                {words.viewReviewed}
              </button>
            </div>
          ) : null}
          {item.reply ? (
            <div className="feedback-staff-reply">
              <strong>{words.replied}</strong>
              <p>{item.reply}</p>
              {item.repliedAt ? (
                <time dateTime={item.repliedAt}>{asMoment(item.repliedAt)}</time>
              ) : null}
            </div>
          ) : null}
          <details>
            <summary>{words.details}</summary>
            <dl>
              <dt>{words.author}</dt>
              <dd>{item.userId}</dd>
              <dt>{words.reference}</dt>
              <dd>{item.id}</dd>
              {Object.entries(item.context).map(([key, value]) => (
                <div className="feedback-metadata" key={key}>
                  <dt>{key}</dt>
                  <dd>{value || "—"}</dd>
                </div>
              ))}
              {item.reviewedAt ? (
                <>
                  <dt>{words.reviewed}</dt>
                  <dd>{asMoment(item.reviewedAt)}</dd>
                  <dt>{words.reviewer}</dt>
                  <dd>{item.reviewedBy}</dd>
                </>
              ) : null}
            </dl>
          </details>
          {can(admin.role, "cases.write") ? (
            <div className="feedback-actions">
              {!item.reply ? (
                <button
                  className="btn btn-quiet"
                  type="button"
                  disabled={Boolean(saving) || pending}
                  onClick={() => void review(item)}
                >
                  {saving === item.id ? words.loading : item.reviewedAt ? words.reopen : words.mark}
                </button>
              ) : null}
              {!item.reply && replyingId !== item.id ? (
                <button
                  className="btn btn-quiet"
                  type="button"
                  disabled={Boolean(saving) || pending}
                  onClick={() => {
                    setReplyDraft("");
                    setReplyingId(item.id);
                  }}
                >
                  {words.reply}
                </button>
              ) : null}
            </div>
          ) : null}
          {replyingId === item.id && !item.reply ? (
            <form
              className="feedback-reply-form"
              onSubmit={(event) => {
                event.preventDefault();
                void sendReply(item);
              }}
            >
              <label htmlFor={`reply-${item.id}`}>{words.replyLabel}</label>
              <p className="dim">{words.replyHint}</p>
              <textarea
                id={`reply-${item.id}`}
                className="input"
                value={replyDraft}
                maxLength={2000}
                minLength={2}
                required
                onChange={(event) => setReplyDraft(event.target.value)}
              />
              <div className="feedback-actions">
                <button
                  className="btn"
                  type="submit"
                  disabled={Boolean(saving) || replyDraft.trim().length < 2}
                >
                  {saving === item.id ? words.loading : words.sendReply}
                </button>
                <button
                  className="btn btn-quiet"
                  type="button"
                  disabled={Boolean(saving)}
                  onClick={() => {
                    setReplyingId(null);
                    setReplyDraft("");
                  }}
                >
                  {words.cancel}
                </button>
              </div>
            </form>
          ) : null}
        </article>
      ))}
      {pending ? (
        <p role="status">{words.loading}</p>
      ) : !items.length && !error ? (
        <p className="empty">{words.empty}</p>
      ) : null}
      {cursor ? (
        <button
          type="button"
          className="btn btn-quiet"
          disabled={pending || Boolean(saving)}
          onClick={() => void load({ ...filters, before: cursor }, true)}
        >
          {words.more}
        </button>
      ) : null}
    </section>
  );
}
